// Native push notifications wiring. The Capacitor plugin and server-side
// register endpoint are in place; until APNS / FCM credentials are added
// to the Render env, the server hook is dormant (no pushes are actually
// dispatched). The flow:
//   1. After the user finishes a meaningful first action (e.g. sends
//      their first chat message), call `requestAndRegisterPush()`.
//   2. The native OS prompts for permission.
//   3. On grant, the device token is sent to /api/push/register so the
//      server can later dispatch pushes.
//
// Web (browser) is a no-op for now — that flow will be added separately
// if/when we want web push.

import { Capacitor } from '@capacitor/core';
import api from './api';

// Server-side dispatch (APNS/FCM) is still a no-op — see pushDispatcher.js.
// Flip this on once real credentials are added to Render, otherwise granting
// users an OS permission prompt for a feature that never delivers anything
// is a broken/deceptive-feature risk for App Review.
const PUSH_DISPATCH_LIVE = false;

const PERMISSION_KEY = 'pnp_push_permission_asked';

let registered = false;

const isNative = () => Capacitor.isNativePlatform();

const platformName = () => {
  const p = Capacitor.getPlatform();
  if (p === 'ios') return 'ios';
  if (p === 'android') return 'android';
  return 'web';
};

const sendTokenToServer = async (token) => {
  try {
    await api.post('/push/register', { token, platform: platformName() });
  } catch (e) {
    // Non-fatal — server may be down or user not yet authed.
    // We'll retry on next app open via initPushIfReady().
  }
};

// Hook the plugin once to forward registration callbacks. Safe to call
// multiple times — the listener bag is idempotent because we set a flag.
let listenersBound = false;
const bindListenersOnce = async () => {
  if (listenersBound || !isNative()) return;
  const { PushNotifications } = await import('@capacitor/push-notifications');
  PushNotifications.addListener('registration', (token) => {
    sendTokenToServer(token.value);
  });
  PushNotifications.addListener('registrationError', () => {
    // Swallow — device is offline, user revoked, etc.
  });
  // Optional: when the user taps a push, route them to the right place.
  PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    const url = action?.notification?.data?.url;
    if (url && typeof window !== 'undefined') {
      window.location.assign(url);
    }
  });
  listenersBound = true;
};

// Ask the OS for permission and (on grant) register with APNS/FCM.
// Idempotent within a session: only prompts once.
export const requestAndRegisterPush = async () => {
  if (!PUSH_DISPATCH_LIVE || !isNative() || registered) return;
  try {
    await bindListenersOnce();
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const status = await PushNotifications.requestPermissions();
    try { localStorage.setItem(PERMISSION_KEY, status?.receive || 'unknown'); } catch {}
    if (status.receive === 'granted') {
      await PushNotifications.register();
      registered = true;
    }
  } catch (e) {
    // Plugin not available, OS denied, etc. — silent fail.
  }
};

// Quietly re-register on app open if the user previously granted permission.
// Doesn't prompt again — just forwards the latest token to the server.
export const initPushIfReady = async () => {
  if (!PUSH_DISPATCH_LIVE || !isNative()) return;
  try {
    const prior = localStorage.getItem(PERMISSION_KEY);
    if (prior !== 'granted') return;
    await bindListenersOnce();
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const status = await PushNotifications.checkPermissions();
    if (status.receive === 'granted') {
      await PushNotifications.register();
      registered = true;
    }
  } catch (e) { /* silent */ }
};
